import React from "react";
import { Box, Button, CircularProgress, Menu, MenuItem } from "@mui/material";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";

function InternalUserActionFooter({
  isSubmitLoading,
  isApproveLoading,
  isRejectLoading,
  isEsignatureLoading,
  isUnderwritingLoading,
  anchorEl,
  underwritingAnchorEl,
  signIndemnityDisabled,
  esignatureButtonLabel,
  handleFinish,
  handleButtonClick,
  handleMenuClose,
  handleOptionSelect,
  setRejectDialogOpen,
  handleUnderwritingMenuClick,
  handleUnderwritingMenuClose,
  handleManualUnderwritingClick,
  handleOverrideUnderwritingClick,
  setIsCreditDialogOpen,
  handleSignIndemnity,
}) {
  return (
    <Box
      className="flex justify-center"
      sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
    >
      <Button
        variant="contained"
        color="primary"
        onClick={handleFinish}
        disabled={isSubmitLoading}
        endIcon={isSubmitLoading && <CircularProgress size={20} />}
      >
        {isSubmitLoading ? "Updating..." : "Update"}
      </Button>

      <Button
        variant="contained"
        color="success"
        onClick={handleButtonClick}
        disabled={isApproveLoading}
        endIcon={
          isApproveLoading ? (
            <CircularProgress size={20} />
          ) : (
            <ArrowDropDownIcon />
          )
        }
      >
        {isApproveLoading ? "Approving…" : "Approve"}
      </Button>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() =>
            handleOptionSelect({
              ams_flow: true,
            })
          }
        >
          Approve with Policy
        </MenuItem>
        <MenuItem
          onClick={() =>
            handleOptionSelect({
              ams_flow: false,
            })
          }
        >
          Approve without Policy
        </MenuItem>
      </Menu>

      <Button
        variant="contained"
        color="error"
        onClick={() => setRejectDialogOpen(true)}
        disabled={isRejectLoading}
        endIcon={isRejectLoading && <CircularProgress size={20} />}
      >
        {isRejectLoading ? "Rejecting..." : "Reject"}
      </Button>

      <Button
        variant="contained"
        color="warning"
        onClick={handleUnderwritingMenuClick}
        disabled={isUnderwritingLoading}
        endIcon={
          isUnderwritingLoading ? (
            <CircularProgress size={20} />
          ) : (
            <ArrowDropDownIcon />
          )
        }
      >
        {isUnderwritingLoading ? "Processing..." : "Underwriting"}
      </Button>

      <Menu
        anchorEl={underwritingAnchorEl}
        open={Boolean(underwritingAnchorEl)}
        onClose={handleUnderwritingMenuClose}
      >
        <MenuItem onClick={handleManualUnderwritingClick}>
          Trigger Underwriting
        </MenuItem>
        <MenuItem onClick={handleOverrideUnderwritingClick}>
          Override Underwriting
        </MenuItem>
        <MenuItem onClick={() => setIsCreditDialogOpen(true)}>
          Upload Credit Report
        </MenuItem>
      </Menu>

      <Button
        variant="contained"
        color="info"
        onClick={handleSignIndemnity}
        disabled={signIndemnityDisabled}
        endIcon={isEsignatureLoading && <CircularProgress size={20} />}
      >
        {isEsignatureLoading ? "Signing..." : esignatureButtonLabel}
      </Button>
    </Box>
  );
}

function CarrierTeamMemberActionFooter({
  isApproveLoading,
  isRejectLoading,
  data,
  handleUpdateUnderwriting,
}) {
  return (
    <Box
      className="flex justify-center"
      sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
    >
      <Button
        variant="contained"
        color="success"
        onClick={() => handleUpdateUnderwriting("approved")}
        disabled={isApproveLoading || !data?.is_underwriting}
        endIcon={isApproveLoading && <CircularProgress size={20} />}
      >
        {isApproveLoading ? "Approving..." : "Approve"}
      </Button>

      <Button
        variant="contained"
        color="error"
        onClick={() => handleUpdateUnderwriting("rejected")}
        disabled={isRejectLoading || !data?.is_underwriting}
        endIcon={isRejectLoading && <CircularProgress size={20} />}
      >
        {isRejectLoading ? "Rejecting..." : "Reject"}
      </Button>
    </Box>
  );
}

/**
 * Internal user + carrier_team_member action buttons/menus for bond retrieval pages.
 */
export function BondRetrievalActionFooter({
  role,
  isSubmitLoading,
  isApproveLoading,
  isRejectLoading,
  isEsignatureLoading,
  isUnderwritingLoading,
  data,
  anchorEl,
  underwritingAnchorEl,
  signIndemnityDisabled,
  esignatureButtonLabel,
  handleFinish,
  handleButtonClick,
  handleMenuClose,
  handleOptionSelect,
  setRejectDialogOpen,
  handleUnderwritingMenuClick,
  handleUnderwritingMenuClose,
  handleManualUnderwritingClick,
  handleOverrideUnderwritingClick,
  setIsCreditDialogOpen,
  handleSignIndemnity,
  handleUpdateUnderwriting,
}) {
  if (role === "super_user" || role === "internal_user") {
    return (
      <InternalUserActionFooter
        isSubmitLoading={isSubmitLoading}
        isApproveLoading={isApproveLoading}
        isRejectLoading={isRejectLoading}
        isEsignatureLoading={isEsignatureLoading}
        isUnderwritingLoading={isUnderwritingLoading}
        anchorEl={anchorEl}
        underwritingAnchorEl={underwritingAnchorEl}
        signIndemnityDisabled={signIndemnityDisabled}
        esignatureButtonLabel={esignatureButtonLabel}
        handleFinish={handleFinish}
        handleButtonClick={handleButtonClick}
        handleMenuClose={handleMenuClose}
        handleOptionSelect={handleOptionSelect}
        setRejectDialogOpen={setRejectDialogOpen}
        handleUnderwritingMenuClick={handleUnderwritingMenuClick}
        handleUnderwritingMenuClose={handleUnderwritingMenuClose}
        handleManualUnderwritingClick={handleManualUnderwritingClick}
        handleOverrideUnderwritingClick={handleOverrideUnderwritingClick}
        setIsCreditDialogOpen={setIsCreditDialogOpen}
        handleSignIndemnity={handleSignIndemnity}
      />
    );
  }

  if (role === "carrier_team_member") {
    return (
      <CarrierTeamMemberActionFooter
        isApproveLoading={isApproveLoading}
        isRejectLoading={isRejectLoading}
        data={data}
        handleUpdateUnderwriting={handleUpdateUnderwriting}
      />
    );
  }

  return null;
}

export default BondRetrievalActionFooter;
